import React, {CSSProperties, FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {createFilterOptions, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import axios, {CancelToken} from "axios";
import PartnerDataGrid, {DataGridColumn, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridRow} from "../../../../../../containers/data-grid";
import BizProductionApiService from "../../../../../../../core/services/api_service/production_api_service";


interface QuoteRow {
    customerName?: string;
    totalQty?: number;
    january?: number;
    february?: number;
    march?: number;
    april?: number;
    may?: number;
    june?: number;
    july?: number;
    august?: number;
    september?: number;
    october?: number;
    november?: number;
    december?: number;
}

export interface PurchasePlanningInformationQuotesDialogProps {
    open: boolean;
    close: () => void;
    partNumber?: string;
    year?: number;
}

const dataGridColumns = {
    customer: "customerName",
    total: "total",
    january: "january",
    february: "february",
    march: "march",
    april: "april",
    may: "may",
    june: "june",
    july: "july",
    august: "august",
    september: "september",
    october: "october",
    november: "november",
    december: "december",
}

const PurchasePlanningDetailsQuotesDialog: FunctionComponent<PurchasePlanningInformationQuotesDialogProps> = (props) => {
    const {close} = props;
    const [rows, setRows] = useState<QuoteRow[]>([]);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    const dataGridSaveConfig = useMemo(() => ({name: `PurchasePlanning_Quotes_DG`, version: 1}), [])
    const dataGridFilter = useMemo(() => createFilterOptions<QuoteRow>({
        ignoreCase: true,
        ignoreAccents: true,
        trim: true,
        stringify: (option) => (option.customerName ?? "")
    }), [])

    const visibleRows = useMemo(() => {
        return dataGridFilter(rows, {
            inputValue: searchText,
            getOptionLabel: (option) => option.customerName ?? "",
        });
    }, [dataGridFilter, rows, searchText])

    /**
     * Resets the import state when the dialog is opened.
     */
    useLayoutEffect(() => {
        if (props.open) {
            setSearchText("");
            setRows([]);
            setLoading(false);
        }
    }, [props.open]);

    /**
     * Fetches the products based on the raw material part number.
     */
    const getQuotes = useCallback(async (PartNo: string, year: number, cancelToken?: CancelToken) => {
        setLoading(true);
        const response = await BizProductionApiService.v1.purchasePlanning.getSalesReport({
            partNumber: PartNo,
            year: year
        }, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted)
            setLoading(false);
        if (response?.resultFlag) {
            setRows(response?.data?.map(e => ({
                customerName: e.customerName,
                totalQty: e.totalQty,
                january: e.salesNumbers.find(s => s.monthIndex === 0)?.totalQty ?? 0,
                february: e.salesNumbers.find(s => s.monthIndex === 1)?.totalQty ?? 0,
                march: e.salesNumbers.find(s => s.monthIndex === 2)?.totalQty ?? 0,
                april: e.salesNumbers.find(s => s.monthIndex === 3)?.totalQty ?? 0,
                may: e.salesNumbers.find(s => s.monthIndex === 4)?.totalQty ?? 0,
                june: e.salesNumbers.find(s => s.monthIndex === 5)?.totalQty ?? 0,
                july: e.salesNumbers.find(s => s.monthIndex === 6)?.totalQty ?? 0,
                august: e.salesNumbers.find(s => s.monthIndex === 7)?.totalQty ?? 0,
                september: e.salesNumbers.find(s => s.monthIndex === 8)?.totalQty ?? 0,
                october: e.salesNumbers.find(s => s.monthIndex === 9)?.totalQty ?? 0,
                november: e.salesNumbers.find(s => s.monthIndex === 10)?.totalQty ?? 0,
                december: e.salesNumbers.find(s => s.monthIndex === 11)?.totalQty ?? 0,
            })) ?? []);
        }
    }, [isMounted])

    /**
     * As soon as the raw material part number changes:
     * - fetch the products based on the part number
     */
    useEffect(() => {
        if (!props?.partNumber || !props?.year) {
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void getQuotes(props.partNumber, props.year, cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [props?.partNumber, props?.year, getQuotes]);


    const columns = useMemo<DataGridColumn[]>(() => [
        {
            name: dataGridColumns.customer,
            title: "Customer",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            width: {minWidth: 150, size: 150, type: DataGridColumnWidthTypes.default},
            resizable: false,
        },
        {
            name: dataGridColumns.total,
            title: "Total",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
        },
        {
            name: dataGridColumns.january,
            title: "January",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.february,
            title: "February",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.march,
            title: "March",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.april,
            title: "April",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.may,
            title: "May",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.june,
            title: "June",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.july,
            title: "July",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.august,
            title: "August",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.september,
            title: "September",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.october,
            title: "October",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.november,
            title: "November",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            name: dataGridColumns.december,
            title: "December",
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false
        }
    ] as DataGridColumn[], [])

    const totalRowsLength = useMemo(() =>
            visibleRows.length + 1 // +1 for the footer row
        , [visibleRows.length])

    const dataGridRows = useMemo<DataGridRow[]>(() => {
        let footerData = {
            [dataGridColumns.january]: 0,
            [dataGridColumns.february]: 0,
            [dataGridColumns.march]: 0,
            [dataGridColumns.april]: 0,
            [dataGridColumns.may]: 0,
            [dataGridColumns.june]: 0,
            [dataGridColumns.july]: 0,
            [dataGridColumns.august]: 0,
            [dataGridColumns.september]: 0,
            [dataGridColumns.october]: 0,
            [dataGridColumns.november]: 0,
            [dataGridColumns.december]: 0,
        }
        const rows = visibleRows.map<DataGridRow>((row) => {
            const inputValue = searchText ?? "";
            const regex = new RegExp(`(${inputValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')})`, 'gi');
            const customerNameParts = row.customerName?.split(regex) ?? [];
            footerData = {
                ...footerData,
                [dataGridColumns.total]: (footerData[dataGridColumns.total] ?? 0) + (row.totalQty ?? 0),
                [dataGridColumns.january]: footerData[dataGridColumns.january] + (row.january ?? 0),
                [dataGridColumns.february]: footerData[dataGridColumns.february] + (row.february ?? 0),
                [dataGridColumns.march]: footerData[dataGridColumns.march] + (row.march ?? 0),
                [dataGridColumns.april]: footerData[dataGridColumns.april] + (row.april ?? 0),
                [dataGridColumns.may]: footerData[dataGridColumns.may] + (row.may ?? 0),
                [dataGridColumns.june]: footerData[dataGridColumns.june] + (row.june ?? 0),
                [dataGridColumns.july]: footerData[dataGridColumns.july] + (row.july ?? 0),
                [dataGridColumns.august]: footerData[dataGridColumns.august] + (row.august ?? 0),
                [dataGridColumns.september]: footerData[dataGridColumns.september] + (row.september ?? 0),
                [dataGridColumns.october]: footerData[dataGridColumns.october] + (row.october ?? 0),
                [dataGridColumns.november]: footerData[dataGridColumns.november] + (row.november ?? 0),
                [dataGridColumns.december]: footerData[dataGridColumns.december] + (row.december ?? 0),
            }
            return ({
                key: row.customerName ?? "",
                data: row,
                cells: {
                    [dataGridColumns.customer]:
                        <Typography variant={"body2"}>
                            {customerNameParts.map((part, index) => {
                                const isOptionPartMatched = part.toLowerCase() === inputValue.toLowerCase();
                                const matchedStyles = {fontWeight: 700, color: 'text.primary',};
                                const unmatchedStyles: CSSProperties = {fontWeight: 400, color: 'text.secondary'};
                                return (
                                    <Typography
                                        key={`_part_${index}`}
                                        variant={'body2'}
                                        component={'span'}
                                        sx={{...(isOptionPartMatched ? matchedStyles : unmatchedStyles)}}
                                    >
                                        {part}
                                    </Typography>
                                );
                            })}
                        </Typography>,
                    [dataGridColumns.total]: row.totalQty?.toString() ?? ' ',
                    [dataGridColumns.january]: row.january?.toString() ?? ' ',
                    [dataGridColumns.february]: row.february?.toString() ?? ' ',
                    [dataGridColumns.march]: row.march?.toString() ?? ' ',
                    [dataGridColumns.april]: row.april?.toString() ?? ' ',
                    [dataGridColumns.may]: row.may?.toString() ?? ' ',
                    [dataGridColumns.june]: row.june?.toString() ?? ' ',
                    [dataGridColumns.july]: row.july?.toString() ?? ' ',
                    [dataGridColumns.august]: row.august?.toString() ?? ' ',
                    [dataGridColumns.september]: row.september?.toString() ?? ' ',
                    [dataGridColumns.october]: row.october?.toString() ?? ' ',
                    [dataGridColumns.november]: row.november?.toString() ?? " ",
                    [dataGridColumns.december]: row.december?.toString() ?? ' ',
                }
            })
        });
        rows.push({
            key: 'footer',
            data: null,
            className: 'data-grid-footer-row',
            cells: {
                [dataGridColumns.customer]: <Typography variant={"body2"} fontWeight={600}>Total:</Typography>,
                ...footerData,
            },
        })
        return rows;
    }, [searchText, visibleRows]);

    return (
        <>
            <Dialog open={props.open} onClose={close} maxWidth={'mdlg'} fullWidth>
                <DialogTitle fontWeight={600}>
                    Quotes Breakdown for {props.partNumber ?? ""} - {props.year ?? ""}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={"Search"}
                        variant={"outlined"}
                        size={"small"}
                        margin={"dense"}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: columns}}
                        columns={columns}
                        rows={dataGridRows}
                        visibleRows={7}
                        hideFooter
                        hideExporting
                        state={{
                            pagination: {currentPage: 1, pageSize: visibleRows.length, length: totalRowsLength},
                            loading: {state: loading},
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0, m: 0}}>
                    <Button
                        variant={'dialog'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PurchasePlanningDetailsQuotesDialog;